import React, { useMemo } from 'react';
import { NavLink as RouterLink, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Button, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import NavItemBadge from './NavItemBadge';

const useStyles = makeStyles()(theme => ({
  item: {
    display: 'flex',
    paddingTop: 0,
    paddingBottom: 0
  },
  button: {
    fontSize: theme.spacing(1.8),
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.primary.contrastText,
    letterSpacing: 0.1,
    justifyContent: 'flex-start',
    padding: '10px 8px',
    textTransform: 'none',
    width: '100%'
  },
  icon: {
    marginRight: theme.spacing(1),
    fill: theme.palette.primary.contrastText
  },
  title: {
    marginRight: 'auto',
    fontSize: theme.spacing(1.8),
    fontWeight: theme.typography.fontWeightMedium,
    letterSpacing: 0.1,
    textTransform: 'none'
  },
  extraMargin: {
    marginRight: theme.spacing(2.2)
  },
  active: {
    color: theme.palette.secondary.main
  },
  activeIcon: {
    fill: theme.palette.secondary.main
  }
}));

const NavItem = ({
  className,
  isRootItem,
  href,
  icon: Icon,
  title,
  toggle,
  openDrawer,
  badgeContent = null,
  exact = false,
  ...rest
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const location = useLocation();
  const isActive = useMemo(
    () =>
      !!href &&
      (exact ? location.pathname === href : location.pathname.startsWith(href)),
    [href, location.pathname, exact]
  );

  return (
    <ListItem
      className={clsx(classes.item, className)}
      disableGutters
      {...rest}
    >
      <NavItemBadge
        badgeContent={badgeContent}
        color="error"
        sx={{ width: '100%' }}
      >
        {toggle ? (
          <Button className={clsx(classes.button, { [classes.active]: isActive })}>
            {Icon && (
              <Icon
                className={clsx(classes.icon, {
                  [classes.extraMargin]: isRootItem,
                  [classes.activeIcon]: isActive
                })}
                size="20"
              />
            )}
            <span className={classes.title}>{t(title)}</span>
          </Button>
        ) : (
          <Button
            className={clsx(classes.button, { [classes.active]: isActive })}
            component={RouterLink}
            to={href}
          >
            {Icon && (
              <ListItemIcon>
                <Icon
                  className={clsx(classes.icon, {
                    [classes.extraMargin]: isRootItem,
                    [classes.activeIcon]: isActive
                  })}
                  size="20"
                />
              </ListItemIcon>
            )}
            <ListItemText>
              <span className={classes.title}>
                {openDrawer ? t(title) : ''}
              </span>
            </ListItemText>
          </Button>
        )}
      </NavItemBadge>
    </ListItem>
  );
};

NavItem.propTypes = {
  className: PropTypes.string,
  href: PropTypes.string,
  icon: PropTypes.elementType,
  title: PropTypes.string,
  toggle: PropTypes.bool,
  isRootItem: PropTypes.bool,
  openDrawer: PropTypes.bool,
  badgeContent: PropTypes.number,
  exact: PropTypes.bool
};

export default NavItem;
